import * as Sentry from '@sentry/browser';
import { Plugin } from './logger';

interface SentryPluginOptions {
  instance?: typeof Sentry;
  defaultScope?: Record<string, any>;
  enableBreadcrumbs?: boolean;
  enableTracing?: boolean;
}

const captureWithScope = (
  event: Error | string,
  scopeData: Record<string, any> = {},
  useWithScope = true,
  isException = true,
) => {
  if (!useWithScope) {
    return isException && event instanceof Error
      ? Sentry.captureException(event)
      : Sentry.captureMessage(String(event));
  }
  return Sentry.withScope((scope) => {
    Object.entries(scopeData).forEach(([key, value]) => {
      if (key === 'tags' && typeof value === 'object') {
        Object.entries(value).forEach(([tagKey, tagValue]) => {
          scope.setTag(tagKey, String(tagValue));
        });
      } else if (key === 'extras' && typeof value === 'object') {
        Object.entries(value).forEach(([extraKey, extraValue]) => {
          scope.setExtra(extraKey, extraValue);
        });
      } else {
        scope.setExtra(key, value);
      }
    });

    if (isException && event instanceof Error) {
      const originalError = event;
      const prefixedError = new Error(`[Kamereo] - ${originalError.message}`);
      prefixedError.stack = originalError.stack;
      prefixedError.name = originalError.name;
      return Sentry.captureException(prefixedError);
    }

    const message = String(event);
    return Sentry.captureMessage(`[Kamereo] - ${message}`);
  });
};

export const createSentryPlugin = (options: SentryPluginOptions = {}): Plugin => {
  const { instance = Sentry, defaultScope = {}, enableBreadcrumbs = true, enableTracing = false } = options;

  const sentryInstance = instance;

  return {
    message: (...args) => {
      return args;
    },

    error: (_prefix, ...args) => {
      const error = args[0];
      const additionalData = args.slice(1);
      const contextData = {
        ...defaultScope,
        additionalInfo: additionalData,
      };

      captureWithScope(error, contextData, true, true);

      if (enableTracing) {
        const transaction = sentryInstance.getCurrentHub()?.getScope()?.getTransaction();
        if (transaction) {
          const span = transaction.startChild({
            op: 'logger.error',
            description: error instanceof Error ? error.message : String(error),
          });
          span.finish();
        }
      }
    },

    warn: (_prefix, ...args) => {
      if (enableBreadcrumbs) {
        sentryInstance.addBreadcrumb({
          category: 'logger',
          message: args.join(' '),
          level: sentryInstance.Severity.Warning,
        });
      }

      const message = args.join(' ');
      const contextData = {
        ...defaultScope,
        level: 'warning',
      };

      captureWithScope(message, contextData, false, false);
    },

    log: (_prefix, ...args) => {
      if (enableBreadcrumbs) {
        sentryInstance.addBreadcrumb({
          category: 'logger',
          message: args.join(' '),
          level: sentryInstance.Severity.Info,
        });
      }
    },
  };
};

export const sentryPlugin = createSentryPlugin();
